import { useEffect, useState } from 'react';
import type { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import { AUTH_LABELS } from '../../../utils/labels';
import type { MemberRole } from '../../../types/roles';

interface UseAuthReturn {
  session: Session | null;
  loading: boolean;
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
  signOut: () => Promise<{ error: AuthError | null }>;
  authError: string | null;
  clearAuthError: () => void;
}

export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [role, setRole] = useState<MemberRole | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const loadProfile = async (userId: string) => {
    try {
      const [{ data: member }, { data: sa }] = await Promise.all([
        supabase
          .from('organization_members')
          .select('organization_id, active, role')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase.from('super_admins').select('user_id').eq('user_id', userId).maybeSingle(),
      ]);

      if (member?.active === false) {
        setAuthError(AUTH_LABELS.accountDeactivated);
        await supabase.auth.signOut();
        // Don't wait on the auth-state-change listener to clear these — its timing
        // relative to this function's `finally` (which flips loading to false) isn't
        // guaranteed, and a stale truthy `session` + unset orgId/role for even one
        // render is enough for ProtectedRoute to render the app in a broken,
        // effectively-read-only state instead of redirecting to /login.
        setSession(null);
        setOrgId(null);
        setIsSuperAdmin(false);
        setRole(null);
        return;
      }

      const isSA = !!sa;
      setIsSuperAdmin(isSA);
      setRole((member?.role as MemberRole | undefined) ?? null);

      if (isSA) {
        const saved = localStorage.getItem('superadmin_org_id');
        if (saved) {
          setOrgId(saved);
        } else {
          const { data: firstOrg } = await supabase
            .from('organizations')
            .select('id')
            .order('name')
            .limit(1)
            .maybeSingle();
          setOrgId(firstOrg?.id ?? null);
        }
      } else {
        setOrgId(member?.organization_id ?? null);
      }
    } catch {
      setAuthError(AUTH_LABELS.loadProfileFailed);
    } finally {
      setProfileLoaded(true);
    }
  };

  useEffect(() => {
    // Tracks which user's profile is currently loaded so re-fires of the same
    // session (see below) can be told apart from an actual sign-in/sign-out.
    let loadedUserId: string | null = null;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s ?? null);
      if (s) {
        loadedUserId = s.user.id;
        loadProfile(s.user.id);
      } else {
        setProfileLoaded(true);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) {
        loadedUserId = null;
        setOrgId(null);
        setIsSuperAdmin(false);
        setRole(null);
        setProfileLoaded(true);
        return;
      }
      // Supabase re-notifies subscribers for the same already-signed-in user
      // whenever the tab regains focus/visibility (a background token refresh,
      // or even just a `SIGNED_IN` re-fire when the token wasn't due for
      // renewal) — not just on an actual sign-in. The session/token itself
      // still needs to update above, but re-running loadProfile and flipping
      // profileLoaded back to false would flip `loading` to true in App.tsx,
      // unmounting the whole app — including any open modal or in-progress
      // form — for a refresh the user never asked for. Only reload the
      // profile when the signed-in user has actually changed.
      if (s.user.id === loadedUserId) return;
      loadedUserId = s.user.id;
      setProfileLoaded(false);
      loadProfile(s.user.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    localStorage.removeItem('superadmin_org_id');
    return supabase.auth.signOut();
  };

  return {
    session: session ?? null,
    loading: session === undefined || !profileLoaded,
    orgId,
    isSuperAdmin,
    role,
    signOut,
    authError,
    clearAuthError: () => setAuthError(null),
  };
}
